<template>
  <tr>
    <th>Keymap for VM</th>
    <td>
      <div ref="keymapPickerRef" class="vm-search-select">
        <input type="hidden" name="user[keymap]" class="vm-search-select-value" :value="selectedKeymap">
        <button
          type="button"
          class="form-control vm-search-select-trigger"
          :aria-expanded="keymapOpen ? 'true' : 'false'"
          @click.stop="toggleKeymapPicker"
        >
          <span class="vm-search-select-trigger-label">{{ selectedKeymapLabel }}</span>
          <span class="caret"></span>
        </button>
        <div v-if="keymapOpen" class="vm-search-select-panel" @click.stop>
          <div class="vm-search-select-options">
            <button
              v-for="option in filteredKeymapOptions"
              :key="`keymap-${option.value}`"
              type="button"
              class="vm-search-select-option"
              :class="{ 'is-selected': selectedKeymap === option.value }"
              @click="selectKeymap(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
      </div>
    </td>
  </tr>

  <tr>
    <th>User Privilege</th>
    <td>
      <template v-if="privilegeReadonly">
        <input type="hidden" name="user[usertype]" :value="selectedUsertype">
        <button type="button" class="form-control vm-search-select-trigger is-readonly">
          <span class="vm-search-select-trigger-label">{{ selectedUsertypeLabel }}</span>
          <span class="caret"></span>
        </button>
      </template>
      <div v-else ref="usertypePickerRef" class="vm-search-select">
        <input type="hidden" name="user[usertype]" class="vm-search-select-value" :value="selectedUsertype">
        <button
          type="button"
          class="form-control vm-search-select-trigger"
          :aria-expanded="usertypeOpen ? 'true' : 'false'"
          @click.stop="toggleUsertypePicker"
        >
          <span class="vm-search-select-trigger-label">{{ selectedUsertypeLabel }}</span>
          <span class="caret"></span>
        </button>
        <div v-if="usertypeOpen" class="vm-search-select-panel" @click.stop>
          <div class="vm-search-select-options">
            <button
              v-for="option in filteredUsertypeOptions"
              :key="`usertype-${option.value}`"
              type="button"
              class="vm-search-select-option"
              :class="{ 'is-selected': selectedUsertype === option.value }"
              @click="selectUsertype(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
      </div>
    </td>
  </tr>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue"

const props = defineProps({
  keymapOptions: {
    type: Array,
    default: () => []
  },
  initialKeymap: {
    type: [String, Number],
    default: ""
  },
  usertypeOptions: {
    type: Array,
    default: () => []
  },
  initialUsertype: {
    type: [String, Number],
    default: ""
  },
  privilegeReadonly: {
    type: Boolean,
    default: false
  }
})

const keymapPickerRef = ref(null)
const usertypePickerRef = ref(null)
const keymapOpen = ref(false)
const usertypeOpen = ref(false)
const selectedKeymap = ref(String(props.initialKeymap || ""))
const selectedUsertype = ref(String(props.initialUsertype || ""))

const normalizedKeymapOptions = computed(() =>
  (props.keymapOptions || []).map((option) => ({
    label: option.label,
    value: String(option.value)
  }))
)

const normalizedUsertypeOptions = computed(() =>
  (props.usertypeOptions || []).map((option) => ({
    label: option.label,
    value: String(option.value)
  }))
)

const filteredKeymapOptions = computed(() => normalizedKeymapOptions.value)

const filteredUsertypeOptions = computed(() => normalizedUsertypeOptions.value)

const selectedKeymapLabel = computed(() => {
  return normalizedKeymapOptions.value.find((option) => option.value === selectedKeymap.value)?.label || "Select a keymap"
})

const selectedUsertypeLabel = computed(() => {
  return normalizedUsertypeOptions.value.find((option) => option.value === selectedUsertype.value)?.label || "Select a user privilege"
})

function toggleKeymapPicker() {
  keymapOpen.value = !keymapOpen.value
  usertypeOpen.value = false
}

function toggleUsertypePicker() {
  usertypeOpen.value = !usertypeOpen.value
  keymapOpen.value = false
}

function selectKeymap(value) {
  selectedKeymap.value = String(value)
  keymapOpen.value = false
}

function selectUsertype(value) {
  selectedUsertype.value = String(value)
  usertypeOpen.value = false
}

function closeOnOutsideClick(event) {
  if (keymapPickerRef.value && !keymapPickerRef.value.contains(event.target)) {
    keymapOpen.value = false
  }

  if (usertypePickerRef.value && !usertypePickerRef.value.contains(event.target)) {
    usertypeOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener("click", closeOnOutsideClick)
})

onBeforeUnmount(() => {
  document.removeEventListener("click", closeOnOutsideClick)
})
</script>
